import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { Formik, Form } from 'formik';
import { useSelector, useDispatch } from 'react-redux';
import IcomoonReact from 'icomoon-react';

import LeaderboardFormFields, { validateLeaderboardForm, DuplicateWarning } from '../components/LeaderboardFormFields';
import LeaderboardTemplateCard from '../components/LeaderboardTemplateCard';
import GroupFilterDropdown from '../components/GroupFilterDropdown';

import Button from '../../../shared/_TBComponents/Button';
import Spinner from '../../../shared/_TBComponents/Spinner';

import {
  fetchExercises,
  addLeaderboard,
  saveTemplate,
} from '../ducks/leaderboardActions';
import { filterAndSortByGroups, findDuplicateTemplate, resolveSubmitGroups } from '../helpers';

import iconSet from '../../../shared/images/teambuildr-selection.json';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: ${({ maxHeight }) => maxHeight || 'none'};
`;

const Header = styled('div')`
  padding: 24px 20px 16px;
  flex-shrink: 0;
`;

const Title = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const Subtitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #9e9e9e;
  margin-top: 2px;
`;

const FilterRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px 12px;
  flex-shrink: 0;
`;

const SearchBar = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 0 10px;
  flex: 1;
`;

const SearchInput = styled('input')`
  border: none;
  outline: none;
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #444;
  background: transparent;
  width: 100%;

  &::placeholder {
    color: #bbb;
  }
`;

const ErrorText = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 13px;
  color: #c62828;
  margin: 8px 0 0;
`;

const ScrollArea = styled('div')`
  flex: 1;
  overflow-y: auto;
`;

const SpinnerOverlay = styled('div')`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NoResultsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 16px;
  padding: 40px 24px;
`;

const NoResultsText = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #999;
  text-align: center;
`;

const GridContainer = styled('div')`
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
`;

const FormHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 20px 0;
  flex-shrink: 0;
`;

const BackButton = styled('button')`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #666;
  padding: 0;

  &:hover {
    color: #222;
  }
`;

const FormTitle = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 24px;
  font-weight: 600;
  color: #444444;
`;

const FormBody = styled('div')`
  padding: 24px 20px;

  form {
    padding-bottom: 0;
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 24px;
`;

const buttonTextProps = { fontWeight: '600', letterSpacing: '0px' };

const AddBoardModal = ({ startInCreate }) => {
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const templates = useSelector((state) => state.leaderboard.data.templates);
  const groups = useSelector((state) => state.leaderboard.data.groups);
  const exercises = useSelector((state) => state.leaderboard.data.exercises);
  const isLoadingExercises = useSelector((state) => state.leaderboard.ui.isLoadingExercises);
  const isLoadingGroups = useSelector((state) => state.leaderboard.ui.isLoadingGroups);
  const isLoadingLeaderboard = useSelector((state) => state.leaderboard.ui.isLoadingLeaderboard);
  const exercisesError = useSelector((state) => state.leaderboard.data.exercisesError);
  const leaderboardError = useSelector((state) => state.leaderboard.data.leaderboardError);
  const saveTemplateError = useSelector((state) => state.leaderboard.data.saveTemplateError);
  const dispatch = useDispatch();
  const isAthlete = currentUser?.admin === 0;
  // Athletes only get the one-time board flow — never the saved-template grid.
  const defaultView = isAthlete || startInCreate || templates.length === 0 ? 'create' : 'grid';
  const [view, setView] = useState(defaultView);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroups, setSelectedGroups] = useState([]);
  const submitActionRef = useRef('one-time');

  const { accountCode } = currentUser;
  const hasTemplates = templates.length > 0;

  const query = searchTerm.trim().toLowerCase();
  const bySearch = searchTerm.length > 0
    ? templates.filter((t) => t.exerciseName.toLowerCase().includes(query))
    : templates;
  const filtered = filterAndSortByGroups(bySearch, selectedGroups);
  const hasFilters = searchTerm.length > 0 || selectedGroups.length > 0;
  const showNoResults = hasFilters && filtered.length === 0;

  const createInitialValues = {
    accountCode,
    category: '',
    exerciseId: 0,
    exerciseName: '',
    groups: [],
    mustBeInAllGroups: false,
    gender: 0,
    rangeType: 5,
    rangeDates: {},
    compareTo: 0,
    compareDates: {},
  };

  const handleLoadTemplate = async (template) => {
    const { id, ...formValues } = template;
    try {
      await dispatch(addLeaderboard(
        { ...formValues, accountCode },
        true,
        id,
      ));
    } catch (err) {
      // leaderboardError set in Redux via FETCH_LEADERBOARD_REJECTED
    }
  };

  const handleCreateSubmit = async (values) => {
    const resolvedValues = { ...values, groups: resolveSubmitGroups(values.groups, groups) };
    const action = submitActionRef.current;
    if (action === 'save-template') {
      let newTemplate;
      try {
        newTemplate = await dispatch(saveTemplate(resolvedValues));
      } catch (err) {
        return; // saveTemplateError set in Redux via SAVE_TEMPLATE_REJECTED
      }
      try {
        await dispatch(addLeaderboard(resolvedValues, true, newTemplate?.id));
      } catch (err) {
        // leaderboardError set in Redux via FETCH_LEADERBOARD_REJECTED
      }
    } else {
      try {
        await dispatch(addLeaderboard(resolvedValues, false));
      } catch (err) {
        // leaderboardError set in Redux via FETCH_LEADERBOARD_REJECTED
      }
    }
  };

  if (view === 'grid') {
    return (
      <Container maxHeight='75vh'>
        <Header>
          <Title>Add Leaderboard</Title>
          <Subtitle>Select a saved template or create a new leaderboard</Subtitle>
        </Header>

        <FilterRow>
          <SearchBar>
            <IcomoonReact iconSet={iconSet} size={14} icon='magnifying-glass-alt' color='black' />
            <SearchInput
              placeholder='Search by exercise...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchBar>
          <GroupFilterDropdown
            groups={groups}
            selectedGroups={selectedGroups}
            onChange={setSelectedGroups}
          />
          <Button
            name='+ Create New'
            bgColor='#444444'
            txtColor='white'
            noBorder
            customBorderRadius='6px'
            styledTextProps={{ fontWeight: 600, fontSize: 14, letterSpacing: '0px' }}
            styledWrapperProps={{ height: '36px', padding: '0 14px', flexShrink: 0 }}
            onClick={() => setView('create')}
          />
        </FilterRow>

        {leaderboardError && (
          <ErrorText style={{ padding: '0 20px 4px' }}>{leaderboardError}</ErrorText>
        )}
        <ScrollArea>
          {isLoadingLeaderboard && <SpinnerOverlay><Spinner /></SpinnerOverlay>}
          {!isLoadingLeaderboard && showNoResults && (
            <NoResultsContainer>
              <NoResultsText>No saved leaderboards match your search.</NoResultsText>
            </NoResultsContainer>
          )}
          {!isLoadingLeaderboard && !showNoResults && (
            <GridContainer>
              {filtered.map((template) => (
                <LeaderboardTemplateCard
                  key={template.id}
                  template={template}
                  onLoad={handleLoadTemplate}
                  readOnly
                />
              ))}
            </GridContainer>
          )}
        </ScrollArea>
      </Container>
    );
  }

  return (
    <Container>
      <FormHeader>
        {hasTemplates && !startInCreate && !isAthlete && (
          <BackButton type='button' aria-label='Back to saved templates' onClick={() => setView('grid')}>
            <IcomoonReact iconSet={iconSet} size={14} icon='left-arrow' color='currentColor' />
          </BackButton>
        )}
        <FormTitle>Create Leaderboard</FormTitle>
      </FormHeader>

      <FormBody>
        {isLoadingLeaderboard ? (
          <SpinnerOverlay><Spinner /></SpinnerOverlay>
        ) : (
          <Formik
            initialValues={createInitialValues}
            validate={validateLeaderboardForm}
            onSubmit={handleCreateSubmit}
          >
            {(formProps) => {
              const { submitForm, isSubmitting } = formProps;
              const submitDisabled = isLoadingLeaderboard || isSubmitting;
              const resolvedFormValues = {
                ...formProps.values,
                groups: resolveSubmitGroups(formProps.values.groups, groups),
              };
              const duplicate = findDuplicateTemplate(resolvedFormValues, templates);
              // Athletes' only option, so it gets primary styling.
              let oneTimeButtonName;
              if (isAthlete) {
                oneTimeButtonName = startInCreate ? 'View Leaderboard' : 'Add Leaderboard';
              } else {
                oneTimeButtonName = startInCreate ? 'View as One-Time Board' : 'Add as One-Time Board';
              }
              const oneTimeButtonStyle = isAthlete
                ? { bgColor: '#444444', txtColor: 'white', noBorder: true }
                : { bgColor: 'white', borderColor: '#444444', txtColor: '#444444' };
              return (
                <Form>
                  <LeaderboardFormFields
                    exercises={exercises}
                    exercisesError={exercisesError}
                    groups={groups}
                    formProps={formProps}
                    isLoadingExercises={isLoadingExercises}
                    isLoadingGroups={isLoadingGroups}
                    onCategoryChange={(category) => dispatch(fetchExercises(accountCode, category))}
                  />
                  {saveTemplateError && <ErrorText>{saveTemplateError}</ErrorText>}
                  {!saveTemplateError && leaderboardError && (
                    <ErrorText>
                      {submitActionRef.current === 'save-template'
                        ? 'Template saved, but the leaderboard failed to load. Please try again.'
                        : leaderboardError}
                    </ErrorText>
                  )}
                  {duplicate && <DuplicateWarning />}
                  <ButtonContainer>
                    {!isAthlete && (
                      <Button
                        name={startInCreate ? 'Save as Template & View' : 'Save Template and Add'}
                        bgColor='#444444'
                        txtColor='white'
                        noBorder
                        fullWidth
                        fontSize='14px'
                        customBorderRadius='4px'
                        styledTextProps={buttonTextProps}
                        disabled={submitDisabled}
                        type='button'
                        onClick={() => {
                          submitActionRef.current = 'save-template';
                          submitForm();
                        }}
                      />
                    )}
                    <Button
                      name={oneTimeButtonName}
                      {...oneTimeButtonStyle}
                      fullWidth
                      fontSize='14px'
                      customBorderRadius='4px'
                      styledTextProps={buttonTextProps}
                      disabled={submitDisabled}
                      type='button'
                      onClick={() => {
                        submitActionRef.current = 'one-time';
                        submitForm();
                      }}
                    />
                  </ButtonContainer>
                </Form>
              );
            }}
          </Formik>
        )}
      </FormBody>
    </Container>
  );
};

AddBoardModal.defaultProps = {
  startInCreate: false,
};

AddBoardModal.propTypes = {
  startInCreate: PropTypes.bool,
};

export default AddBoardModal;
